// ============================================================
// PERRY — THE DROWNED MILE
// Level 2's world. Everything the Cinders Pass learned, said in
// the opposite temperature.
//
// THE ARGUMENT OF THIS FILE, because a biome is an argument:
//
// The Cinders Road is a country the fire HAPPENED TO. It is hot,
// it is orange, it is textured to death — ash grain, wheel ruts,
// kerb stones, drag marks — and every one of those details is
// evidence of something that went through here.
//
// The Drowned Mile is the one part of the country the fire could
// not be bothered with, because there was nothing here dry enough
// to interest it. So it is COLD, it is nearly black, and it is
// almost featureless — and the few things in it are enormous and
// unexplained: a chimney standing in open water with no house
// under it, a bell on its side in the shallows, a chapel that was
// deliberately flooded by the people who built it.
//
// Which means the visual budget goes somewhere completely
// different. On the road it went into SURFACE. Here it goes into
// REFLECTION and SILHOUETTE, because that is what water is for:
// one low moon, one mirror, and everything else read as a shape
// against it.
//
// LAWS, all carried in unchanged from the Cinders Pass:
//   • Light budget UNTOUCHED at seven. Nothing here adds a light.
//     Every glow in this file is emissive, additive or a sprite.
//   • mulberry32 for every plank, reed and post. The fen must look
//     the same fen twice.
//   • InstancedMesh for anything there is more than thirty of.
//     ~1,600 objects in five draw calls.
//   • Canvas textures cached at module scope in the shared TEX,
//     tagged SRGBColorSpace — except normal maps, which are data.
//   • Fog-exempt for anything past the fog wall, or the silhouette
//     layers that sell the depth get eaten by the depth.
//   • No boxes. Not one, anywhere, including the duckboards —
//     they are flattened cylinders, which additionally gives them
//     a rounded worn edge for free.
// ============================================================

import { useRef, useMemo } from "react";
import * as THREE from "three";
import { useFrame } from "@react-three/fiber";
import { mulberry32 } from "./qteEngine";
import { TEX, NOISE, fbm, normalMapFrom, heightCanvas, fillInstances, softSprite, softBand } from "./worldTex";

// The fen's atmosphere, exported so the stage can set them without
// knowing anything about what is in here. Colder and CLOSER than
// the road's: 10 → 62 against 12 → 74, because standing water eats
// distance and because a horizon four feet away in every direction
// is the entire feeling of the act.
export const FEN_FOG = ["#0a1416", 10, 62];
// (the fen's sky gradient lives in FenSky below — the stage never
//  needs it, only the fog)

const INK = "#050a0b";

// ── THE MUD ───────────────────────────────────────────────────
// Not ash. Ash is grey and takes light evenly; fen mud is nearly
// black, faintly green, and WET, which means it is darker than it
// should be and shinier than it should be at the same time.
function mudCanvas(size = 512) {
  const c = document.createElement("canvas");
  c.width = c.height = size;
  const g = c.getContext("2d");
  g.fillStyle = "#141c18";
  g.fillRect(0, 0, size, size);
  for (let y = 0; y < size; y += 2) {
    for (let x = 0; x < size; x += 2) {
      const n = fbm(NOISE, x * 0.035, y * 0.035, 4);
      const w = fbm(NOISE, x * 0.008, y * 0.008, 3);   // broad wet patches
      const v = Math.floor(14 + n * 26 + w * 12);
      g.fillStyle = `rgb(${v},${Math.floor(v * 1.22)},${Math.floor(v * 1.05)})`;
      g.fillRect(x, y, 2, 2);
    }
  }
  // rotted vegetable matter — the fen is made of what it ate
  const R = mulberry32(7741);
  for (let i = 0; i < 900; i++) {
    const x = R() * size, y = R() * size;
    const l = 3 + R() * 11, a = R() * Math.PI;
    g.strokeStyle = `rgba(${40 + R() * 30 | 0},${52 + R() * 34 | 0},${34 + R() * 20 | 0},${0.10 + R() * 0.2})`;
    g.lineWidth = 0.6 + R() * 1.1;
    g.beginPath();
    g.moveTo(x, y);
    g.lineTo(x + Math.cos(a) * l, y + Math.sin(a) * l);
    g.stroke();
  }
  return c;
}
function mudTexture() {
  if (!TEX.fenMud) {
    const t = new THREE.CanvasTexture(mudCanvas());
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    t.repeat.set(9, 9);
    t.colorSpace = THREE.SRGBColorSpace;
    TEX.fenMud = t;
  }
  return TEX.fenMud;
}
function mudNormal() {
  if (!TEX.fenMudN) {
    const t = normalMapFrom(heightCanvas(256, 991, 300, 22), 2.2);
    t.repeat.set(9, 9);
    TEX.fenMudN = t;
  }
  return TEX.fenMudN;
}

// ── THE WATER ─────────────────────────────────────────────────
// One plane, one normal map, and a scroll. That is the whole
// recipe and it is the correct one for this engine: a mirror does
// not need an albedo, it needs a SURFACE NORMAL and something
// bright to bend. The moon is already there, raking in low, and
// the moment the ripples have direction the fen reads as wet.
//
// TWO normal maps at different scales and opposite drift speeds
// is the trick that stops it looking like a scrolling texture —
// a big slow swell under a small fast chop. Costs one extra
// sampler and buys the entire effect.
function rippleNormal(key, seed, blobs, big, rep, strength) {
  if (!TEX[key]) {
    const t = normalMapFrom(heightCanvas(256, seed, blobs, big), strength);
    t.repeat.set(rep, rep);
    TEX[key] = t;
  }
  return TEX[key];
}

function BlackWater() {
  const nA = rippleNormal("fenRipA", 4242, 220, 30, 26, 1.5);
  const nB = rippleNormal("fenRipB", 8181, 520, 12, 62, 0.9);
  const matA = useRef();
  const matB = useRef();

  useFrame((state) => {
    const t = state.clock.elapsedTime;
    // the swell goes one way, the chop the other, and neither of
    // them at a speed you can name
    nA.offset.set(t * 0.0075, t * 0.0042);
    nB.offset.set(-t * 0.0210, t * 0.0138);
    if (matA.current) matA.current.needsUpdate = false;
    if (matB.current) matB.current.needsUpdate = false;
  });

  return (
    <group>
      {/* the mirror. Low roughness + high metalness is what makes
          a MeshStandardMaterial behave like still water under a
          single hard light. */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[26, -0.075, -6]} receiveShadow>
        <planeGeometry args={[220, 190, 1, 1]} />
        <meshStandardMaterial
          ref={matA}
          color="#0b1a1e"
          metalness={0.92}
          roughness={0.16}
          normalMap={nA}
          normalScale={new THREE.Vector2(0.55, 0.55)}
          envMapIntensity={1.2}
        />
      </mesh>
      {/* the chop, as a second nearly-transparent sheet a
          millimetre above it. Additive would blow out; this is a
          normal-mapped glaze. */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[26, -0.068, -6]}>
        <planeGeometry args={[220, 190, 1, 1]} />
        <meshStandardMaterial
          ref={matB}
          color="#12262a"
          metalness={0.85}
          roughness={0.34}
          normalMap={nB}
          normalScale={new THREE.Vector2(0.32, 0.32)}
          transparent
          opacity={0.55}
          depthWrite={false}
        />
      </mesh>
    </group>
  );
}

// ── THE BANKS ─────────────────────────────────────────────────
// Displaced mud either side of the causeway, dug UP rather than
// down — the fen's ground is what is left when the water has taken
// the rest, so it stands in humps and islands instead of rolling.
function FenBanks() {
  const geo = useMemo(() => {
    const g = new THREE.PlaneGeometry(200, 170, 108, 92);
    const p = g.attributes.position;
    for (let i = 0; i < p.count; i++) {
      const x = p.getX(i), y = p.getY(i);
      const wx = x + 26, wz = -y - 6;
      // distance from the causeway corridor, which is drowned flat
      const corridor = Math.min(1, Math.abs(wz) / 5.5);
      const n = fbm(NOISE, wx * 0.055, wz * 0.055, 4);
      const island = Math.pow(fbm(NOISE, wx * 0.017, wz * 0.017, 3), 2.1);
      let h = (n - 0.42) * 0.7 + island * 1.5;
      h *= corridor;                 // the mile itself stays open water
      h -= 0.32 * (1 - corridor);    // ...and is scooped, not flat
      p.setZ(i, h);
    }
    g.computeVertexNormals();
    return g;
  }, []);

  return (
    <mesh
      geometry={geo}
      rotation={[-Math.PI / 2, 0, 0]}
      position={[26, -0.14, -6]}
      receiveShadow
    >
      <meshStandardMaterial
        map={mudTexture()}
        normalMap={mudNormal()}
        normalScale={new THREE.Vector2(1.05, 1.05)}
        roughness={0.78}
        metalness={0.14}
        color="#8fa89c"
      />
    </mesh>
  );
}

// ── THE CAUSEWAY ──────────────────────────────────────────────
// "The road stops being a road and becomes a promise about where a
// road used to be. Boards, mostly. Some of the boards are doors."
//
// Duckboards laid over trestles, most of them crooked, a few of
// them missing, and every twelfth one visibly a door somebody took
// off a house. All of it in two draw calls.
function Causeway() {
  const boards = useRef();
  const piles = useRef();
  const doors = useRef();

  const sets = useMemo(() => {
    const R = mulberry32(5150);
    const b = [], p = [], d = [];
    for (let x = -6; x < 62; x += 0.44) {
      if (R() < 0.05) continue;                       // a gap. Mind it.
      const sag = Math.sin(x * 0.7) * 0.012 + (R() - 0.5) * 0.02;
      const skew = (R() - 0.5) * 0.14;
      const isDoor = R() < 0.075;
      const wob = Math.sin(x * 0.19) * 0.5;           // the mile wanders
      // ── LAID FLAT — fixed 2026-07-29 ──────────────────────
      // A three.js cylinder's axis is Y, and nothing here ever
      // turned it, so every "duckboard" was a 1.62-tall hexagonal
      // POST standing 0.80 proud of the walk line — 155 of them in
      // a row. A picket fence, not a boardwalk.
      //
      // The trestle piles are the proof of intent: they run y −1.00
      // to 0.00, i.e. they top out exactly AT the walk line, which
      // is only sensible if they are holding something up there.
      //
      // rotX(π/2) sends local +Y to world +Z, so the board spans the
      // causeway's WIDTH. Local X then reads as width-along-the-road
      // and local Z as thickness, which is why the scale triple is
      // reordered below. The skew still yaws each plank a little,
      // because a straight boardwalk in a fen is a lie.
      const item = {
        p: [x, -0.012 + sag, wob + (R() - 0.5) * 0.06],
        r: [Math.PI / 2 + skew * 0.4, skew, (R() - 0.5) * 0.05],
        s: isDoor
          ? [1.25, 1, 0.17]                           // wider, and thin
          : [0.9 + R() * 0.3, 1, 0.24],               // ~0.09 thick plank
      };
      (isDoor ? d : b).push(item);
      if (R() < 0.34) {
        const side = R() < 0.5 ? -1 : 1;
        p.push({
          p: [x + (R() - 0.5) * 0.2, -0.5, wob + side * (0.72 + R() * 0.12)],
          r: [(R() - 0.5) * 0.22, R() * 3, (R() - 0.5) * 0.22],
          s: [1, 0.8 + R() * 0.7, 1],
        });
      }
    }
    return { b, p, d };
  }, []);

  const plank = useMemo(() => new THREE.MeshStandardMaterial({
    map: mudTexture(), color: "#7a6a55", roughness: 0.92, metalness: 0.04,
  }), []);
  const soaked = useMemo(() => new THREE.MeshStandardMaterial({
    map: mudTexture(), color: "#5f5343", roughness: 0.96, metalness: 0.02,
  }), []);

  return (
    <group>
      {/* the boards — flattened cylinders, so the worn round edge
          is geometry rather than a texture claiming there is one */}
      <instancedMesh
        ref={(el) => fillInstances(el, sets.b)}
        args={[null, null, sets.b.length]}
        castShadow receiveShadow material={plank}
      >
        <cylinderGeometry args={[0.19, 0.19, 1.62, 6]} />
      </instancedMesh>
      {/* the doors. Wider, darker, and once you notice one you
          start looking for the others. */}
      <instancedMesh
        ref={(el) => fillInstances(el, sets.d)}
        args={[null, null, Math.max(1, sets.d.length)]}
        castShadow receiveShadow material={soaked}
      >
        <cylinderGeometry args={[0.30, 0.30, 1.74, 4]} />
      </instancedMesh>
      {/* the trestle piles going down into the dark */}
      <instancedMesh
        ref={(el) => fillInstances(el, sets.p)}
        args={[null, null, Math.max(1, sets.p.length)]}
        castShadow material={soaked}
      >
        <cylinderGeometry args={[0.045, 0.062, 1.0, 5]} />
      </instancedMesh>
    </group>
  );
}

// ── THE REEDS ─────────────────────────────────────────────────
// Sixteen hundred of them in one draw call. Tapered cones, leaning
// with a seeded bias so a bed reads as having a prevailing wind,
// and a second shorter tier so the beds have understory instead of
// looking like a bed of nails.
function ReedBeds() {
  const tall = useRef();
  const short = useRef();

  const sets = useMemo(() => {
    const R = mulberry32(3312);
    const a = [], b = [];
    for (let i = 0; i < 1500; i++) {
      const x = -8 + R() * 74;
      const z = (R() - 0.5) * 78;
      const corridor = Math.abs(z - Math.sin(x * 0.19) * 0.5);
      if (corridor < 1.35) continue;                   // keep the mile clear
      // reeds like the shallows: dense at the margin, thin far out
      const density = Math.exp(-Math.abs(corridor - 5.5) / 9);
      if (R() > density) continue;
      const lean = 0.10 + R() * 0.24;
      const face = R() * Math.PI * 2;
      const item = {
        p: [x, -0.06, z],
        r: [Math.sin(face) * lean, face, Math.cos(face) * lean],
        s: [0.8 + R() * 0.5, 0.55 + R() * 1.05, 0.8 + R() * 0.5],
      };
      (R() < 0.62 ? a : b).push(item);
    }
    return { a, b };
  }, []);

  const reed = useMemo(() => new THREE.MeshStandardMaterial({
    color: "#5c6a4a", roughness: 0.95, metalness: 0.02,
    emissive: new THREE.Color("#16241c"), emissiveIntensity: 0.35,
  }), []);
  const dead = useMemo(() => new THREE.MeshStandardMaterial({
    color: "#7a6c4e", roughness: 0.98, metalness: 0.01,
  }), []);

  return (
    <group>
      <instancedMesh
        ref={(el) => fillInstances(el, sets.a)}
        args={[null, null, Math.max(1, sets.a.length)]}
        castShadow material={reed}
      >
        <coneGeometry args={[0.022, 1.35, 4]} />
      </instancedMesh>
      <instancedMesh
        ref={(el) => fillInstances(el, sets.b)}
        args={[null, null, Math.max(1, sets.b.length)]}
        castShadow material={dead}
      >
        <coneGeometry args={[0.016, 0.78, 3]} />
      </instancedMesh>
    </group>
  );
}

// ── MOORING POSTS ─────────────────────────────────────────────
// The fen's fenceline. There is no fence — there are only the
// posts, standing in open water, marking a boundary between two
// pieces of water that nobody alive can tell apart.
//
// One of them, at x=20, is J'ynxi's. The stage puts her on it.
function MooringPosts() {
  const posts = useRef();
  const caps = useRef();
  const sets = useMemo(() => {
    const R = mulberry32(6060);
    const p = [], c = [];
    for (let x = -4; x < 64; x += 3.1) {
      const side = R() < 0.5 ? -1 : 1;
      const z = Math.sin(x * 0.19) * 0.5 + side * (2.6 + R() * 5.5);
      const lean = (R() - 0.5) * 0.34;
      const h = 0.7 + R() * 1.1;
      // The post is a 1.5-tall cylinder scaled by h about its own
      // centre, so its TOP is at -0.3 + 0.75h. The cap used to be
      // placed at 0.62h — 0.13h low, which for a tall post is more
      // than twice the cap sphere's radius, so every rotted cap in
      // the fen was buried inside the post it was meant to crown.
      const px = x + R();
      p.push({ p: [px, -0.3, z], r: [lean, R() * 3, lean * 0.7], s: [1, h, 1] });
      c.push({ p: [px, -0.3 + h * 0.75, z], r: [lean, R() * 3, lean * 0.7], s: [1, 1, 1] });
    }
    return { p, c };
  }, []);
  const wood = useMemo(() => new THREE.MeshStandardMaterial({
    map: mudTexture(), color: "#5a4e3e", roughness: 0.97, metalness: 0.03,
  }), []);
  const rot = useMemo(() => new THREE.MeshStandardMaterial({
    color: "#3d4038", roughness: 1, metalness: 0,
  }), []);
  return (
    <group>
      <instancedMesh
        ref={(el) => fillInstances(el, sets.p)}
        args={[null, null, sets.p.length]}
        castShadow material={wood}
      >
        <cylinderGeometry args={[0.072, 0.095, 1.5, 6]} />
      </instancedMesh>
      {/* the rotted cap — every post in a fen is soft on top */}
      <instancedMesh
        ref={(el) => fillInstances(el, sets.c)}
        args={[null, null, sets.c.length]}
        material={rot}
      >
        <sphereGeometry args={[0.085, 7, 5]} />
      </instancedMesh>
    </group>
  );
}

// ── THE THINGS THE WATER KEPT ─────────────────────────────────
// "The fen does not bury things. It just keeps them at the depth
//  it found them."
//
// So: a chimney with no house under it, a roof ridge at 40°, a
// cartwheel, and the bell. Hand-placed, not scattered — each one
// is meant to be LOOKED at, and a scatter never is.
function SunkenThings() {
  const stone = useMemo(() => new THREE.MeshStandardMaterial({
    map: mudTexture(), color: "#6b6a63", roughness: 0.9, metalness: 0.06,
  }), []);
  const thatch = useMemo(() => new THREE.MeshStandardMaterial({
    color: "#5e5238", roughness: 1, metalness: 0,
  }), []);
  const brass = useMemo(() => new THREE.MeshStandardMaterial({
    color: "#6e6242", metalness: 0.9, roughness: 0.44,
    emissive: new THREE.Color("#2a2410"), emissiveIntensity: 0.3,
  }), []);

  return (
    <group>
      {/* the chimney — nothing under it, and that is the point */}
      <group position={[9.4, 0, -5.2]} rotation={[0.05, 0.4, 0.07]}>
        <mesh castShadow position={[0, 1.05, 0]} material={stone}>
          <cylinderGeometry args={[0.34, 0.44, 2.3, 5]} />
        </mesh>
        <mesh castShadow position={[0, 2.28, 0]} material={stone}>
          <cylinderGeometry args={[0.4, 0.36, 0.22, 5]} />
        </mesh>
      </group>

      {/* a roof ridge, at the angle it went in at */}
      <group position={[17.5, -0.2, 5.6]} rotation={[0, -0.5, 0.30]}>
        <mesh castShadow rotation={[0, 0, 0]} material={thatch}>
          <coneGeometry args={[1.5, 1.5, 4]} />
        </mesh>
        <mesh castShadow position={[0, 0.02, 0]} scale={1.03} material={thatch}>
          <coneGeometry args={[1.5, 1.5, 4]} />
        </mesh>
      </group>

      {/* a cartwheel, upright, half under */}
      <mesh castShadow position={[23.2, 0.16, -3.9]} rotation={[0.2, 0.6, 0.34]} material={stone}>
        <torusGeometry args={[0.52, 0.055, 6, 16]} />
      </mesh>

      {/* ── THE BELL ── on its side in the shallows, with a fish
             living in it. Sister Ashlin will explain the chapel;
             nobody explains the fish. */}
      <group position={[37.4, -0.02, 2.4]} rotation={[1.42, 0.3, 0.2]}>
        <mesh castShadow material={brass}>
          <cylinderGeometry args={[0.62, 0.42, 0.86, 14, 1, true]} />
        </mesh>
        <mesh castShadow position={[0, 0.5, 0]} material={brass}>
          <torusGeometry args={[0.13, 0.045, 6, 12]} />
        </mesh>
        {/* the dark inside it, which is where the fish is */}
        <mesh position={[0, -0.1, 0]} rotation={[Math.PI / 2, 0, 0]}>
          <circleGeometry args={[0.6, 16]} />
          <meshBasicMaterial color="#02090a" side={THREE.DoubleSide} />
        </mesh>
      </group>
    </group>
  );
}

// ── THE DROWNED CHAPEL ────────────────────────────────────────
// The boss arena, and the only architecture on the level. It is
// half under: the nave is gone, the gable end and one wall of the
// tower are still up, and the tower leans about eight degrees,
// which is enough to notice and not enough to look comic.
//
// Sister Ashlin's line in Candlemarch is the whole design brief —
// "the water did not take that chapel. The water was invited, by
// us, in a hurry." So it is not RUINED. It is intact and drowned,
// which is a completely different silhouette: clean lines, sharp
// arches, standing in the wrong medium.
function DrownedChapel({ x = 41 }) {
  const stone = useMemo(() => new THREE.MeshStandardMaterial({
    map: mudTexture(), color: "#7d7c72", roughness: 0.86, metalness: 0.08,
  }), []);
  const dark = useMemo(() => new THREE.MeshBasicMaterial({ color: INK }), []);
  const glassMat = useMemo(() => new THREE.MeshBasicMaterial({
    color: "#a06ac0", transparent: true, opacity: 0.24, toneMapped: false, depthWrite: false,
  }), []);

  return (
    <group position={[x, 0, -2.6]}>
      {/* ── the gable end, standing square ── */}
      <mesh castShadow receiveShadow position={[0, 1.5, 0]} scale={[1, 1, 0.22]} material={stone}>
        <cylinderGeometry args={[1.55, 1.7, 3.0, 6]} />
      </mesh>
      {/* the arch, cut as a dark disc rather than boolean'd out —
          nothing behind it but fen, and fen is already black */}
      <mesh position={[0, 1.35, 0.2]} scale={[0.52, 1.15, 1]} material={dark}>
        <circleGeometry args={[0.62, 18]} />
      </mesh>
      {/* the rose window above it, still with something in it */}
      <mesh position={[0, 2.6, 0.2]} material={glassMat}>
        <circleGeometry args={[0.42, 20]} />
      </mesh>
      <mesh position={[0, 2.6, 0.19]} rotation={[0, 0, 0]} material={stone}>
        <torusGeometry args={[0.44, 0.05, 6, 20]} />
      </mesh>
      {[0, 1, 2].map((i) => (
        <mesh key={i} position={[0, 2.6, 0.2]} rotation={[0, 0, (i * Math.PI) / 3]} material={stone}>
          <cylinderGeometry args={[0.022, 0.022, 0.86, 4]} />
        </mesh>
      ))}

      {/* ── the tower, leaning ── */}
      <group position={[-2.4, 0, -1.1]} rotation={[0.03, 0.2, 0.14]}>
        <mesh castShadow receiveShadow position={[0, 2.4, 0]} material={stone}>
          <cylinderGeometry args={[0.72, 0.82, 4.8, 7]} />
        </mesh>
        {/* the belfry openings — four dark arches, no bell in any
            of them, because the bell is at x=37 lying on its side */}
        {[0, 1, 2, 3].map((i) => (
          <mesh key={i} position={[
            Math.sin((i * Math.PI) / 2) * 0.7, 4.3, Math.cos((i * Math.PI) / 2) * 0.7,
          ]} rotation={[0, (i * Math.PI) / 2, 0]} scale={[0.34, 0.9, 1]} material={dark}>
            <circleGeometry args={[0.4, 12]} />
          </mesh>
        ))}
        <mesh castShadow position={[0, 5.3, 0]} material={stone}>
          <coneGeometry args={[0.95, 1.5, 7]} />
        </mesh>
      </group>

      {/* ── the drowned nave: pillar stumps in a row, going under
             at a constant rate, which is how you draw "this
             continues past where you can see" in three objects ── */}
      {[1.9, 3.4, 4.9, 6.4].map((dz, i) => (
        <group key={i}>
          {[-1.15, 1.15].map((sx) => (
            <mesh
              key={sx} castShadow position={[sx, 0.62 - i * 0.17, dz]}
              material={stone}
            >
              <cylinderGeometry args={[0.19, 0.23, 1.3 - i * 0.26, 8]} />
            </mesh>
          ))}
        </group>
      ))}

      {/* the waterline stain across all of it, one band, one mesh.
          Sat at y 0.13 — which is 0.205 ABOVE the water plane, so it
          read as a black plate hovering over the fen rather than a
          darkening in it. It belongs a hair above the water, not a
          hair above the chapel's floor. (DIAL, reversible.) */}
      <mesh position={[0, -0.06, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <ringGeometry args={[0, 9, 24]} />
        <meshBasicMaterial color="#04100f" transparent opacity={0.34} depthWrite={false} />
      </mesh>
    </group>
  );
}

// ── THE MOUNTAIN ──────────────────────────────────────────────
// Level 3, visible from Level 2, foreshadowed in the walk line:
// "the ground finally gets up again, and keeps going, and becomes
//  a mountain with a hole burned through the cloud above it."
//
// Fog-exempt, additive glow at the summit, and a real hole in the
// cloud deck — a torus seen face-on with nothing inside it, which
// is the cheapest and most ominous shape in the whole file.
function TheMountain() {
  const rock = useMemo(() => new THREE.MeshBasicMaterial({
    color: "#0d1216", fog: false, toneMapped: false,
  }), []);
  const rock2 = useMemo(() => new THREE.MeshBasicMaterial({
    color: "#121a1e", fog: false, toneMapped: false,
  }), []);
  const cloud = useMemo(() => new THREE.MeshBasicMaterial({
    color: "#1c1a26", fog: false, transparent: true, opacity: 0.9,
    toneMapped: false, depthWrite: false,
  }), []);
  const burn = useMemo(() => new THREE.MeshBasicMaterial({
    color: "#d43a24", fog: false, transparent: true, opacity: 0.5,
    toneMapped: false, depthWrite: false, blending: THREE.AdditiveBlending,
  }), []);

  return (
    <group position={[96, 0, -74]}>
      {/* foothills first, so the peak has something to rise out of */}
      <mesh position={[-26, 5, 14]} material={rock2}>
        <coneGeometry args={[24, 13, 7]} />
      </mesh>
      <mesh position={[26, 4, 16]} material={rock2}>
        <coneGeometry args={[21, 10, 6]} />
      </mesh>
      {/* the mountain */}
      <mesh position={[0, 17, 0]} material={rock}>
        <coneGeometry args={[30, 40, 8]} />
      </mesh>
      <mesh position={[-9, 11, 8]} material={rock}>
        <coneGeometry args={[15, 25, 7]} />
      </mesh>
      {/* the hole burned through the cloud deck */}
      <group position={[0, 44, 2]} rotation={[Math.PI / 2.2, 0, 0]}>
        <mesh material={cloud}>
          <torusGeometry args={[16, 9, 8, 26]} />
        </mesh>
        <mesh material={burn}>
          <torusGeometry args={[16, 2.2, 6, 26]} />
        </mesh>
      </group>
      {/* the light coming up out of it. It is not a light. */}
      {/* Unmapped, this was a 26 x 18 SOLID RED RECTANGLE hanging
          in the sky — the largest single object in the level and
          the loudest thing wrong with it. */}
      <sprite position={[0, 38, 1]} scale={[26, 18, 1]}>
        <spriteMaterial
          map={softSprite()}
          color="#c8341c" transparent opacity={0.20} fog={false}
          depthWrite={false} toneMapped={false}
          blending={THREE.AdditiveBlending}
        />
      </sprite>
    </group>
  );
}

// ── CANDLEMARCH ───────────────────────────────────────────────
// The far bank, and the payoff of the whole level: a hillside of
// small lit windows, none of which belong to anybody in
// particular, because the lamplighters do the entire town every
// night for travellers who have not arrived yet.
//
// Two hundred and forty windows in one draw call. This is the one
// place in either biome where instancing is doing something
// EMOTIONAL rather than something technical — the point is the
// count. Twelve windows is a hamlet. Two hundred is a promise.
function Candlemarch({ x = 60 }) {
  const win = useRef();
  const items = useMemo(() => {
    const R = mulberry32(1212);
    const out = [];
    for (let i = 0; i < 240; i++) {
      const u = R();
      const hx = x + 2 + u * 26;
      const hill = Math.pow(1 - Math.abs(u - 0.42) * 1.5, 2);
      const hy = 0.6 + R() * Math.max(0.4, hill * 13);
      const hz = -14 + R() * 30;
      out.push({ p: [hx, hy, hz], r: [0, 0, 0], s: [1, 0.7 + R() * 0.7, 1] });
    }
    return out;
  }, [x]);

  const lit = useMemo(() => new THREE.MeshBasicMaterial({
    color: "#ffcf86", fog: false, toneMapped: false,
    transparent: true, opacity: 0.95,
  }), []);
  const mass = useMemo(() => new THREE.MeshBasicMaterial({
    color: "#0e1114", fog: false, toneMapped: false,
  }), []);

  return (
    <group>
      {/* the hill itself, as a silhouette — it must not be lit or
          the windows stop being the brightest thing on it */}
      <mesh position={[x + 15, 3, 4]} material={mass}>
        <coneGeometry args={[22, 15, 9]} />
      </mesh>
      <mesh position={[x + 4, 1.5, -12]} material={mass}>
        <coneGeometry args={[12, 8, 7]} />
      </mesh>
      <instancedMesh
        ref={(el) => fillInstances(el, items)}
        args={[null, null, items.length]}
        material={lit}
      >
        <planeGeometry args={[0.13, 0.19]} />
      </instancedMesh>
      {/* the glow the whole hill makes together */}
      {/* Same again and worse: 46 x 22, a slab of flat orange
          sitting where the destination of the entire act is
          supposed to be glowing over a hill. */}
      <sprite position={[x + 15, 6, 2]} scale={[46, 22, 1]}>
        <spriteMaterial
          map={softSprite()}
          color="#ffb45e" transparent opacity={0.16} fog={false}
          depthWrite={false} toneMapped={false}
          blending={THREE.AdditiveBlending}
        />
      </sprite>
    </group>
  );
}

// ── WILL-O-LIGHTS ─────────────────────────────────────────────
// The fen's own coals, and a deliberate visual rhyme: six small
// cold lights hanging over black water, at head height, drifting.
// A player who has spent the Cinders Road learning to read six
// small lights will look straight at these, and they mean nothing
// whatsoever, and that is the joke the level is making about the
// coals two fights before it takes them away.
//
// Sprites. Additive. No light spent.
function WillOLights({ count = 26 }) {
  const grp = useRef();
  const seeds = useMemo(() => {
    const R = mulberry32(9090);
    return Array.from({ length: count }, () => ({
      x: -6 + R() * 70,
      z: (R() - 0.5) * 44,
      y: 0.35 + R() * 1.4,
      sp: 0.12 + R() * 0.4,
      ph: R() * 6.28,
      sc: 0.16 + R() * 0.3,
      hue: R() < 0.75 ? "#7fe8c8" : "#a9c8ff",
    }));
  }, [count]);

  useFrame((state) => {
    const g = grp.current;
    if (!g) return;
    const t = state.clock.elapsedTime;
    for (let i = 0; i < g.children.length; i++) {
      const s = seeds[i];
      const c = g.children[i];
      c.position.set(
        s.x + Math.sin(t * s.sp + s.ph) * 1.6,
        s.y + Math.sin(t * s.sp * 1.7 + s.ph) * 0.18,
        s.z + Math.cos(t * s.sp * 0.8 + s.ph) * 1.2
      );
      const f = 0.55 + Math.sin(t * 2.1 + s.ph) * 0.3 + Math.sin(t * 5.3 + s.ph) * 0.14;
      c.material.opacity = Math.max(0.08, f * 0.5);
      c.scale.setScalar(s.sc * (0.85 + f * 0.4));
    }
  });

  return (
    <group ref={grp}>
      {seeds.map((s, i) => (
        <sprite key={i} position={[s.x, s.y, s.z]} scale={s.sc}>
          {/* A spriteMaterial with NO MAP draws a flat opaque
              square. Twenty-six of them over black water is what
              "the level looks basic" actually looks like: teal
              confetti. The map is the whole fix. */}
          <spriteMaterial
            map={softSprite()}
            color={s.hue} transparent opacity={0.4} depthWrite={false}
            toneMapped={false} blending={THREE.AdditiveBlending}
          />
        </sprite>
      ))}
    </group>
  );
}

// ── THE MIST ──────────────────────────────────────────────────
// Heavier than the Cinders Road's, and LOWER — it sits ON the
// water rather than between the ridges, which is the difference
// between atmosphere and weather. Four bands, drifting at four
// speeds, fog-exempt so the fog wall does not eat them.
function FenMist() {
  const grp = useRef();
  const bands = useMemo(() => ([
    // Was z 10. Every camera mark in the game sits between z 1.6
    // and 9.5 looking toward the player at z ~0, so a curtain at
    // z 10 was always BEHIND the lens — the one band doing the
    // "mist sits ON the water" work never drew a pixel. Moved
    // just upstage of the action instead of just behind the eye.
    { z: -4, y: 0.24, w: 130, h: 3.2, o: 0.16, sp: 0.05 },
    { z: -14, y: 0.5, w: 150, h: 4.6, o: 0.20, sp: -0.035 },
    { z: -30, y: 1.1, w: 170, h: 6.5, o: 0.22, sp: 0.024 },
    { z: -50, y: 2.2, w: 200, h: 9.0, o: 0.24, sp: -0.016 },
  ]), []);

  useFrame((state) => {
    const g = grp.current;
    if (!g) return;
    const t = state.clock.elapsedTime;
    for (let i = 0; i < g.children.length; i++) {
      g.children[i].position.x = 26 + Math.sin(t * bands[i].sp) * 16;
    }
  });

  return (
    <group ref={grp}>
      {bands.map((b, i) => (
        <mesh key={i} position={[26, b.y, b.z]}>
          <planeGeometry args={[b.w, b.h]} />
          {/* Same disease, bigger surface: a flat colour on a
              plane is a RECTANGLE, and a hard-edged grey rectangle
              hanging over a fen is the single most expensive thing
              in the frame. `softBand` fades it out on all four
              sides, thickest low, so it reads as weather. */}
          <meshBasicMaterial
            map={softBand()}
            color="#9fb6b4" transparent opacity={b.o} fog={false}
            depthWrite={false} toneMapped={false}
          />
        </mesh>
      ))}
    </group>
  );
}

// ── MIDGES ────────────────────────────────────────────────────
// The fen's answer to the road's ash motes. Smaller, faster,
// clustered in columns over the water rather than drifting evenly,
// because that is what they actually do and because a column of
// them catching the moon is free depth cueing.
function Midges({ count = 460 }) {
  const pts = useRef();
  const { geo, mat, seeds } = useMemo(() => {
    const R = mulberry32(2727);
    const pos = new Float32Array(count * 3);
    const sd = new Float32Array(count * 3);
    const cols = 26;
    for (let i = 0; i < count; i++) {
      const c = Math.floor(R() * cols);
      const cx = -6 + (c / cols) * 70 + R() * 2.2;
      const cz = (R() - 0.5) * 34;
      pos[i * 3] = cx;
      pos[i * 3 + 1] = 0.1 + R() * 1.5;
      pos[i * 3 + 2] = cz;
      sd[i * 3] = R() * 6.28;
      sd[i * 3 + 1] = 1.4 + R() * 3.4;
      sd[i * 3 + 2] = 0.05 + R() * 0.11;
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    const m = new THREE.PointsMaterial({
      color: "#b9d6cc", size: 0.028, transparent: true, opacity: 0.5,
      depthWrite: false, toneMapped: false, blending: THREE.AdditiveBlending,
    });
    return { geo: g, mat: m, seeds: sd };
  }, [count]);

  useFrame((state) => {
    const p = pts.current;
    if (!p) return;
    const t = state.clock.elapsedTime;
    const a = p.geometry.attributes.position;
    for (let i = 0; i < count; i++) {
      const ph = seeds[i * 3], sp = seeds[i * 3 + 1], r = seeds[i * 3 + 2];
      a.array[i * 3] += Math.sin(t * sp + ph) * r * 0.02;
      a.array[i * 3 + 1] += Math.cos(t * sp * 1.3 + ph) * r * 0.014;
      a.array[i * 3 + 2] += Math.cos(t * sp + ph) * r * 0.02;
    }
    a.needsUpdate = true;
  });

  return <points ref={pts} geometry={geo} material={mat} />;
}

// ── THE FEN'S SKY ─────────────────────────────────────────────
// Same law as the road's: fog false, depthWrite false, BackSide,
// renders first. Different gradient — no orange anywhere in it,
// because the one warm thing on this level is Perry's own ember
// and the one lit thing on the horizon is Candlemarch.
function FenSky() {
  const tex = useMemo(() => {
    if (TEX.fenSky) return TEX.fenSky;
    const c = document.createElement("canvas");
    c.width = 16; c.height = 256;
    const g = c.getContext("2d");
    const grad = g.createLinearGradient(0, 0, 0, 256);
    grad.addColorStop(0.00, "#03070a");
    grad.addColorStop(0.42, "#08131a");
    grad.addColorStop(0.72, "#122229");
    grad.addColorStop(0.90, "#1b2f33");
    grad.addColorStop(1.00, "#0a1416");
    g.fillStyle = grad;
    g.fillRect(0, 0, 16, 256);
    const t = new THREE.CanvasTexture(c);
    t.colorSpace = THREE.SRGBColorSpace;
    TEX.fenSky = t;
    return t;
  }, []);
  return (
    <mesh scale={[-1, 1, 1]}>
      <sphereGeometry args={[210, 20, 16]} />
      <meshBasicMaterial map={tex} side={THREE.BackSide} fog={false} depthWrite={false} toneMapped={false} />
    </mesh>
  );
}

// ════════════════════════════════════════════════════════════
// THE WORLD
// ════════════════════════════════════════════════════════════
// One export, no props but Perry's position ref, and it adds
// EXACTLY ZERO LIGHTS. The Rig in CinematicStage still owns all
// seven and always will; everything in here is emissive, additive,
// a sprite, or a silhouette.
export default function FenWorld({ posRef }) {
  return (
    <group>
      <FenSky />
      <TheMountain />
      <Candlemarch x={58} />
      <FenBanks />
      <BlackWater />
      <Causeway />
      <ReedBeds />
      <MooringPosts />
      <SunkenThings />
      <DrownedChapel x={41} />
      <FenMist />
      <WillOLights count={26} />
      <Midges count={460} />
    </group>
  );
}
